/**
 * Outreach cockpit types and labels.
 *
 * Hand-written rather than generated, like `lead-inbox.ts`: the browser
 * Supabase client has no `<Database>` generic (see `src/lib/supabase.ts`), so
 * `crm_outreach` needs a row shape here. Every field mirrors
 * `supabase/migrations/20260731103000_crm_outreach.sql` and its follow-up
 * migrations (…110000 admin-only RLS, …120000 claim/finalize, …140000
 * connection ladder) column for column — those migrations are the source of
 * truth.
 *
 * The state ladder models the real LinkedIn sequence: you cannot message a
 * non-connection, so a connection request (with a ~300-char note) has to be
 * sent and accepted before the actual message can go out. See
 * `20260731140000_crm_outreach_connection_ladder.sql` for the full rationale.
 */

import { differenceInCalendarDays } from 'date-fns';
import {
  CONNECTION_NOTE_CHAR_LIMIT,
  OUTREACH_BRANDS,
  type OutreachBrand,
} from './outreach-seed.js';

export { OUTREACH_BRANDS };
export type { OutreachBrand };

/**
 * LinkedIn's connection-request note cap. The UI must never let this be a
 * surprise. Declared in `outreach-seed.js` — the seed reports how many notes
 * exceed it before a write, and this module already imports from there, so the
 * constant lives at the bottom of the dependency edge and is re-exported here
 * for the UI's existing import site.
 */
export { CONNECTION_NOTE_CHAR_LIMIT };

export const OUTREACH_STATES = [
  'needs_connection',
  'connection_sent',
  'connected',
  'sent',
  'skipped',
] as const;
export type OutreachState = (typeof OUTREACH_STATES)[number];

export const OUTREACH_MESSAGE_ORIGINS = ['mirror_original', 'recrafted', 'manual'] as const;
export type OutreachMessageOrigin = (typeof OUTREACH_MESSAGE_ORIGINS)[number];

export interface OutreachRow {
  id: string;
  organization_id: string;

  brand: OutreachBrand;

  dedupe_key: string;
  linkedin_url: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  job_title: string | null;

  source_campaign_key: string | null;

  message_text: string;
  message_origin: OutreachMessageOrigin;
  followup_text: string | null;

  connection_note: string | null;
  connection_sent_at: string | null;
  connected_at: string | null;
  declined_at: string | null;

  state: OutreachState;
  skipped_reason: string | null;
  sent_at: string | null;
  sent_by: string | null;

  contact_id: string | null;
  claimed_by: string | null;
  claimed_until: string | null;

  seed_run_id: string | null;
  created_at: string;
  updated_at: string;
}

export const OUTREACH_BRAND_LABELS: Record<OutreachBrand, string> = {
  qualiteh: 'Qualiteh',
  agenticmotion: 'AgenticMotion',
  msp_deliveryos: 'MSP DeliveryOS',
};

export const OUTREACH_STATE_LABELS: Record<OutreachState, string> = {
  needs_connection: 'Needs connection request',
  connection_sent: 'Awaiting acceptance',
  connected: 'Connected — ready to message',
  sent: 'Sent',
  skipped: 'Skipped',
};

export const OUTREACH_MESSAGE_ORIGIN_LABELS: Record<OutreachMessageOrigin, string> = {
  mirror_original: 'Original',
  recrafted: 'Recrafted',
  manual: 'Manual',
};

/**
 * First-of-this-month / first-of-next-month boundaries, in the operator's
 * own local calendar. `Date`'s local constructors + `toISOString()` do the
 * local-to-UTC conversion a `timestamptz` comparison against
 * `connection_sent_at` needs. Used by the notes-spent-this-month count —
 * see `idx_crm_outreach_note_spend` in
 * `20260731150000_crm_outreach_note_budget.sql`.
 */
export function currentMonthRange(now: Date): { start: string; end: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
  return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * How long a connection request has been waiting for a response, in the
 * plain words the `connection_sent` rung shows at a glance — the only
 * signal this screen has that an invite is not coming.
 */
export function describeWaitDuration(sentAt: string | null, now: Date = new Date()): string {
  if (!sentAt) return 'waiting since an unknown time';
  const sentDate = new Date(sentAt);
  if (!Number.isFinite(sentDate.getTime())) return 'waiting since an unknown time';

  const days = differenceInCalendarDays(now, sentDate);
  if (days <= 0) return 'waiting less than a day';
  if (days === 1) return 'waiting 1 day';
  return `waiting ${days} days`;
}

export const WAIT_URGENCY_LEVELS = ['quiet', 'normal', 'stale'] as const;
export type WaitUrgency = (typeof WAIT_URGENCY_LEVELS)[number];

/**
 * Recent is quiet, three weeks unanswered is not — the only styling
 * decision the `connection_sent` rung's waiting line makes.
 */
export function waitUrgency(sentAt: string | null, now: Date = new Date()): WaitUrgency {
  if (!sentAt) return 'normal';
  const sentDate = new Date(sentAt);
  if (!Number.isFinite(sentDate.getTime())) return 'normal';

  const days = differenceInCalendarDays(now, sentDate);
  if (days < 7) return 'quiet';
  if (days >= 21) return 'stale';
  return 'normal';
}

/** Display name for a queue row; never empty, so a row is always identifiable. */
export function outreachDisplayName(
  row: Pick<OutreachRow, 'first_name' | 'last_name' | 'linkedin_url'>,
): string {
  const name = [row.first_name, row.last_name]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part !== '')
    .join(' ');

  if (name !== '') return name;

  // Fall back to the LinkedIn vanity slug — the one identity every row has.
  return row.linkedin_url.replace(/^https:\/\/www\.linkedin\.com\/in\//, '') || row.linkedin_url;
}
